import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

/**
 * UI State Interface
 * Manages transient UI state that doesn't need to persist
 */
interface UIState {
  // Sidebar state
  isSidebarOpen: boolean;
  toggleSidebar: () => void;
  setSidebarOpen: (open: boolean) => void;

  // Modal state
  activeModal: string | null;
  modalData: any;
  openModal: (modalId: string, data?: any) => void;
  closeModal: () => void;

  // Toast/notification state
  toasts: Toast[];
  addToast: (toast: Omit<Toast, 'id' | 'timestamp'>) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;

  // Loading states
  globalLoading: boolean;
  setGlobalLoading: (loading: boolean) => void;

  // Theme
  theme: 'light' | 'dark' | 'system';
  setTheme: (theme: 'light' | 'dark' | 'system') => void;

  // Workspace context
  workspaceContext: 'personal' | 'company' | 'college';
  selectedOrgId: string | null;
  selectedOrgName: string | null;
  selectedOrgType: 'COMPANY' | 'COLLEGE' | null;
  selectedOrgLogoUrl: string | null;
  /** URL slug of the selected organization (workspace URLs are /college/<slug>/…). */
  selectedOrgSlug: string | null;
  setWorkspaceContext: (
    context: 'personal' | 'company' | 'college',
    org?: {
      id: string;
      name: string;
      type: 'COMPANY' | 'COLLEGE';
      logoUrl?: string | null;
      slug?: string | null;
    }
  ) => void;
  clearWorkspaceContext: () => void;

  // Notification preferences (persisted)
}

interface Toast {
  id: string;
  type: 'success' | 'error' | 'warning' | 'info';
  message: string;
  duration?: number;
  timestamp: number;
}

/**
 * UI Store (Zustand)
 *
 * Purpose: Manage client-side UI state that doesn't need to be synced with server
 *
 * Use Cases:
 * - Sidebar open/close state
 * - Modal visibility and data
 * - Toast notifications
 * - Loading overlays
 * - Theme preferences
 * - Workspace context (persisted)
 *
 * Why Zustand?
 * - Minimal boilerplate
 * - No providers needed
 * - TypeScript-first
 * - DevTools support
 * - Works outside React (can be called from API layer)
 */
export const useUIStore = create<UIState>()(
  devtools(
    persist(
      (set) => ({
        // Sidebar
        isSidebarOpen: true,
        toggleSidebar: () => set((state) => ({ isSidebarOpen: !state.isSidebarOpen })),
        setSidebarOpen: (open) => set({ isSidebarOpen: open }),

        // Modal
        activeModal: null,
        modalData: null,
        openModal: (modalId, data = null) => set({ activeModal: modalId, modalData: data }),
        closeModal: () => set({ activeModal: null, modalData: null }),

        // Toasts
        toasts: [],
        addToast: (toast) =>
          set((state) => ({
            toasts: [
              ...state.toasts,
              {
                ...toast,
                id: `${Date.now()}-${Math.random()}`,
                timestamp: Date.now(),
              },
            ],
          })),
        removeToast: (id) =>
          set((state) => ({
            toasts: state.toasts.filter((t) => t.id !== id),
          })),
        clearToasts: () => set({ toasts: [] }),

        // Global loading
        globalLoading: false,
        setGlobalLoading: (loading) => set({ globalLoading: loading }),

        // Theme
        theme: 'system',
        setTheme: (theme) => set({ theme }),

        // Workspace context
        workspaceContext: 'personal',
        selectedOrgId: null,
        selectedOrgName: null,
        selectedOrgType: null,
        selectedOrgLogoUrl: null,
        selectedOrgSlug: null,
        setWorkspaceContext: (context, org) =>
          set({
            workspaceContext: context,
            selectedOrgId: org?.id ?? null,
            selectedOrgName: org?.name ?? null,
            selectedOrgType: org?.type ?? null,
            selectedOrgLogoUrl: org?.logoUrl ?? null,
            selectedOrgSlug: org?.slug ?? null,
          }),
        clearWorkspaceContext: () =>
          set({
            workspaceContext: 'personal',
            selectedOrgId: null,
            selectedOrgName: null,
            selectedOrgType: null,
            selectedOrgLogoUrl: null,
            selectedOrgSlug: null,
          }),
      }),
      {
        name: 'welynk-workspace',
        partialize: (state) => ({
          workspaceContext: state.workspaceContext,
          selectedOrgId: state.selectedOrgId,
          selectedOrgName: state.selectedOrgName,
          selectedOrgType: state.selectedOrgType,
          selectedOrgLogoUrl: state.selectedOrgLogoUrl,
          selectedOrgSlug: state.selectedOrgSlug,
          theme: state.theme,
        }),
      }
    ),
    { name: 'UI Store' }
  )
);
